import _ from 'lodash'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useAudioInputs, useAudioOutputs, useMeetingManager, useVideoInputs } from 'amazon-chime-sdk-component-library-react'
import { DeviceType } from 'amazon-chime-sdk-component-library-react/lib/types'
import Fab from '@mui/material/Fab'
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined'

import ControlBarItem from '@/components/chime-sdk/controls/ControlBarItem'
import DeviceSelectionDialog, { DeviceProps } from '@/components/ui/dialogs/DeviceSelectionDialog'

import { useLocalStorage } from '@/hooks/useLocalStorage'

import { rejectDefaultDevices } from '@/utils/devices'

export type DeviceSelectionControlProps = {
  customDevices?: DeviceProps[]
  onSelectDeviceError?: (error: unknown) => void
  showCustomDevices?: boolean
}

export default function DeviceSelectionControl(props: DeviceSelectionControlProps) {
  const { customDevices = [], onSelectDeviceError, showCustomDevices } = props
  const meetingManager = useMeetingManager()
  const { devices: audioInputDevices, selectedDevice: selectedAudioInputDevice } = useAudioInputs()
  const { devices: audioOutputDevices, selectedDevice: selectedAudioOuputDeviceId } = useAudioOutputs()
  const { devices: videoInputDevices, selectedDevice: selectedVideoInputDevice } = useVideoInputs()

  const selectedAudioInputDeviceId = typeof selectedAudioInputDevice === 'string' ? selectedAudioInputDevice : null
  const selectedVideoInputDeviceId = typeof selectedVideoInputDevice === 'string' ? selectedVideoInputDevice : null

  const [persistedAudioInput, setPersistedAudioInput] = useLocalStorage('audioinput', selectedAudioInputDeviceId)
  const [persistedAudioOutput, setPersistedAudioOutput] = useLocalStorage('audiooutput', selectedAudioOuputDeviceId)
  const [persistedVideoInput, setPersistedVideoInput] = useLocalStorage('videoinput', selectedVideoInputDeviceId)

  const isPersistedAudioInputUsed = useRef(false)
  const isPersistedAudioOutputUsed = useRef(false)
  const isPersistedVideoInputUsed = useRef(false)

  const [openDialog, setOpenDialog] = useState<boolean>(false)

  const devices = useMemo(() => {
    const mapMediaDeviceToDeviceProps = (mediaDevices: DeviceType[], type: MediaDeviceKind, selectedDeviceId: string | null) =>
      _.map(mediaDevices, ({ deviceId, label }) => ({
        id: deviceId,
        label: label,
        isCustom: false,
        isSelected: deviceId === selectedDeviceId,
        type,
      }))
    const uniqueDevices = _.uniqBy(
      [
        ..._.map(customDevices, (customDevice) => ({
          ...customDevice,
          isSelected: [selectedAudioInputDeviceId, selectedAudioOuputDeviceId, selectedVideoInputDeviceId].some(
            (selectedDeviceId) => selectedDeviceId === customDevice.id
          ),
        })),
        ...mapMediaDeviceToDeviceProps(audioInputDevices, 'audioinput', selectedAudioInputDeviceId),
        ...mapMediaDeviceToDeviceProps(audioOutputDevices, 'audiooutput', selectedAudioOuputDeviceId),
        ...mapMediaDeviceToDeviceProps(videoInputDevices, 'videoinput', selectedVideoInputDeviceId),
      ],
      'id'
    )
    return uniqueDevices
  }, [
    audioInputDevices,
    audioOutputDevices,
    customDevices,
    videoInputDevices,
    selectedAudioInputDeviceId,
    selectedAudioOuputDeviceId,
    selectedVideoInputDeviceId,
  ])

  useEffect(() => {
    if (!meetingManager) return

    if (!!persistedAudioInput && !isPersistedAudioInputUsed.current) {
      meetingManager.startAudioInputDevice(persistedAudioInput)
      isPersistedAudioInputUsed.current = true
    }

    if (!!persistedAudioOutput && !isPersistedAudioOutputUsed.current) {
      meetingManager.startAudioOutputDevice(persistedAudioOutput)
      isPersistedAudioOutputUsed.current = true
    }

    if (!!persistedVideoInput && !isPersistedVideoInputUsed.current) {
      meetingManager.startVideoInputDevice(persistedVideoInput)
      isPersistedVideoInputUsed.current = true
    }
  }, [meetingManager, persistedAudioInput, persistedAudioOutput, persistedVideoInput])

  const handleSelectDevice = async (deviceId: string, type: MediaDeviceKind) => {
    if (selectedAudioInputDeviceId === deviceId || selectedAudioOuputDeviceId === deviceId || selectedVideoInputDeviceId === deviceId) return
    try {
      switch (type) {
        case 'audioinput':
          await meetingManager.startAudioInputDevice(deviceId)
          setPersistedAudioInput(deviceId)
          break
        case 'audiooutput':
          await meetingManager.startAudioOutputDevice(deviceId)
          setPersistedAudioOutput(deviceId)
          break
        case 'videoinput':
          await meetingManager.startVideoInputDevice(deviceId)
          setPersistedVideoInput(deviceId)
          break
        default:
          break
      }
    } catch (error) {
      onSelectDeviceError?.(error)
    }
  }

  const handleToggleDialog = () => {
    setOpenDialog(!openDialog)
  }

  return (
    <>
      <ControlBarItem>
        <Fab color="primary" onClick={handleToggleDialog} size="small">
          <SettingsOutlinedIcon />
        </Fab>
      </ControlBarItem>
      <DeviceSelectionDialog
        devices={devices}
        onClose={handleToggleDialog}
        onSelectDevice={handleSelectDevice}
        open={openDialog}
        showCustomDevices={showCustomDevices}
        context="visioConference"
      />
    </>
  )
}
